import React from 'react';

export interface FilterChip {
    // Unique across the whole panel, e.g. `approval_status:APPROVED`.
    key: string;
    label: string;
    onRemove: () => void;
}

interface FilterChipsContextValue {
    chips: FilterChip[];
    // Each facet publishes its current active chips under a stable source key
    // (usually its search param). Publishing an empty array clears that source.
    publish: (source: string, chips: FilterChip[]) => void;
}

const noop = () => undefined;

const FilterChipsContext = React.createContext<FilterChipsContextValue>({
    chips: [],
    publish: noop
});

// Wraps the filter panel so every facet rendered inside it (passed down as the
// `filters` node) can report its active selections as removable chips, without
// the page having to describe them. Facets used outside a panel get the no-op
// default, so nothing breaks.
export const FilterChipsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [sources, setSources] = React.useState<Record<string, FilterChip[]>>({});

    const publish = React.useCallback((source: string, chips: FilterChip[]) => {
        setSources((prev) => {
            const current = prev[source] ?? [];
            // Skip no-op updates so a facet re-render doesn't loop the provider.
            const same =
                current.length === chips.length &&
                current.every((c, i) => c.key === chips[i].key && c.label === chips[i].label);
            if (same) {
                return prev;
            }
            return { ...prev, [source]: chips };
        });
    }, []);

    const chips = React.useMemo(() => Object.values(sources).flat(), [sources]);

    const value = React.useMemo(() => ({ chips, publish }), [chips, publish]);

    return <FilterChipsContext.Provider value={value}>{children}</FilterChipsContext.Provider>;
};

// Publishes a facet's active chips, and clears them on unmount.
//
// Keyed on the chips' contents rather than the array identity: callers rebuild the array every
// render, so a caller whose memo breaks would re-run this effect constantly, and each re-run
// clears (`[]`) then republishes — two provider updates that re-render the caller, which breaks
// its memo again. That loop froze the page as soon as a facet had one active chip. The ref keeps
// the published array current without making identity a trigger.
export const usePublishFilterChips = (source: string, chips: FilterChip[]) => {
    const { publish } = React.useContext(FilterChipsContext);
    const chipsRef = React.useRef(chips);
    chipsRef.current = chips;
    const signature = chips.map((chip) => `${chip.key}=${chip.label}`).join('|');

    React.useEffect(() => {
        publish(source, chipsRef.current);
        return () => publish(source, []);
    }, [publish, source, signature]);
};

export const useFilterChips = () => React.useContext(FilterChipsContext).chips;

export default FilterChipsContext;
